import { recordError } from '@util/Errors';
import { isDefined } from 'ts-utils';
import { hasReferralInURL } from '../../config/AssessmentUtils';
import { Experiment, ExperimentConfig, VariantOf, VariantsConfig } from './Experiment';

type FlowConfigExperimentConfig<Variants extends VariantsConfig> = Omit<
  ExperimentConfig<Variants>,
  'variants'
> & {
  nonReferredVariants: Partial<Variants>;
  referredVariants: Partial<Variants>;
};

/** Persists assigned variant in localStorage; first visit picks randomly from referred vs non-referred pools. */
export class FlowConfigExperiment<Variants extends VariantsConfig> extends Experiment<Variants> {
  public readonly nonReferredVariants: Partial<Variants>;
  public readonly referredVariants: Partial<Variants>;

  public constructor(config: FlowConfigExperimentConfig<Variants>) {
    const { nonReferredVariants, referredVariants, ...baseExperimentConfig } = config;
    const variants = { ...nonReferredVariants, ...referredVariants } as Variants;
    super({ ...baseExperimentConfig, variants });
    this.nonReferredVariants = nonReferredVariants;
    this.referredVariants = referredVariants;
  }

  protected getVariantFromSource(): VariantOf<Variants> | undefined {
    const activeVariants = Object.values(
      hasReferralInURL() ? this.referredVariants : this.nonReferredVariants
    ) as VariantOf<Variants>[];
    if (activeVariants.length === 0) {
      return undefined;
    }

    const storedVariant = this.retrieveStoredVariant();
    // storedVariant could be stale - We need to check that the stored variant is still available
    // given the current referral status.
    if (isDefined(storedVariant) && activeVariants.includes(storedVariant as VariantOf<Variants>)) {
      return storedVariant as VariantOf<Variants>;
    }

    const variant = this.selectRandomVariant(activeVariants);
    this.persistVariant(variant);

    return variant;
  }

  private get storageKey(): string {
    return `flowExperiment:${this.name}`;
  }

  private retrieveStoredVariant(): string | undefined {
    try {
      return localStorage.getItem(this.storageKey) ?? undefined;
    } catch (error) {
      recordError(error);
    }
  }

  private persistVariant(variant: string): void {
    try {
      localStorage.setItem(this.storageKey, variant);
    } catch (error) {
      recordError(error);
    }
  }

  private selectRandomVariant(variantsValues: VariantOf<Variants>[]): VariantOf<Variants> {
    return variantsValues[Math.floor(Math.random() * variantsValues.length)];
  }

  public reset(): void {
    super.reset();
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      recordError(error);
    }
  }
}
